import * as React from 'react';
import * as Sentry from '@sentry/react';
import { isRouteErrorResponse, useLocation, useNavigate, useRouteError } from 'react-router-dom';
import { ErrorState } from '@/components/ui/error-state';
import { NotFoundPage } from './pages/NotFoundPage';

/**
 * `errorElement` for every route in `router.tsx`.
 *
 * Without it react-router's own `DefaultErrorComponent` catches a route's
 * render/loader/`lazy()` failure AND an unknown path first, before
 * `AppErrorBoundary` (`main.tsx`) ever sees either: that default screen is in
 * English, renders outside `AppLayout`, and never reaches Sentry. This
 * component replaces it wherever `router.tsx` attaches an `errorElement`.
 *
 * A 404 — `isRouteErrorResponse(error) && error.status === 404` — is not a
 * defect, it is an unknown URL (the same case `router.tsx`'s trailing `*`
 * route handles directly; this path only matters when a PARENT route's
 * `errorElement` is the one asked to render, e.g. no matching route at all),
 * so it is never reported: it renders `NotFoundPage` and returns.
 *
 * Anything else is a real failure. It is reported to Sentry exactly once — a
 * `useEffect` keyed on the error, not on every render — and shown with the
 * same title `AppErrorBoundary` uses for a render error React catches outside
 * the router, so the two error surfaces read as one.
 */
export function RouteErrorPage(): React.JSX.Element {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;

  React.useEffect(() => {
    if (isNotFound) return;
    Sentry.captureException(error);
  }, [error, isNotFound]);

  if (isNotFound) {
    return <NotFoundPage />;
  }

  return (
    // No `min-h-screen` here, unlike `AppErrorBoundary`: that boundary renders
    // INSTEAD of the document, while this one renders (in its main case) inside
    // `AppLayout`'s content column, which `app-shell.tsx` already declares
    // `min-h-screen` beneath a 48 px header — a second one would make every
    // in-shell route error a ~100vh + 48px document with a scrollbar and a card
    // below the true centre.
    <div className="flex items-center justify-center p-inset">
      <ErrorState
        error={error}
        title="Algo se rompió en esta pantalla"
        // A reload would lose a half-filled form — the same reason
        // `AppErrorBoundary`'s retry remounts instead of reloading.
        // `navigate` to the SAME location re-runs the route (and its
        // `lazy()` import) without a full page reload.
        onRetry={() => navigate(location, { replace: true })}
      />
    </div>
  );
}
